import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTeams } from "../lib/teams";
import { PlanProvider } from "../lib/usePlan";
import RenewalBanner from "./billing/RenewalBanner";
import {
  Trophy,
  Users,
  ClipboardList,
  CalendarCheck,
  Dumbbell,
  Layout,
  LogOut,
  Plus,
  ChevronDown,
  Home,
  CalendarDays,
  BarChart3,
  UserCog,
  MapPin,
  BookOpen,
  ShieldCheck,
  Wallet,
  NotebookPen,
  Eye,
  Settings,
  Bell,
  Menu,
  HeartHandshake,
  Hotel,
  FileSignature,
  Sparkles,
  CreditCard,
  Building2,
  MessageCircle,
  FileCheck2,
  Trash2,
} from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import api from "../lib/api";
import { toast } from "sonner";
import NewTeamDialog from "./layout/NewTeamDialog";
import EditTeamDialog from "./layout/EditTeamDialog";
import OnboardingWizard from "./onboarding/OnboardingWizard";
import PWAInstallPrompt from "./pwa/PWAInstallPrompt";

const NAV = [
  { to: "/app", label: "Dashboard", icon: Home, testid: "nav-dashboard" },
  { to: "/app/anagrafica", label: "Anagrafica", icon: Users, testid: "nav-players" },
  { to: "/app/staff", label: "Staff Tecnico", icon: ShieldCheck, testid: "nav-staff" },
  { to: "/app/genitori", label: "Anagrafica Genitori", icon: HeartHandshake, testid: "nav-parents" },
  { to: "/app/tornei", label: "Gestione Tornei", icon: Hotel, testid: "nav-tournaments" },
  { to: "/app/modulistica", label: "Modulistica", icon: FileSignature, testid: "nav-modulistica" },
  { to: "/app/firme", label: "Firme & Audit", icon: FileCheck2, testid: "nav-signatures" },
  { to: "/app/mister-ai", label: "Mister AI", icon: Sparkles, testid: "nav-mister-ai" },
  { to: "/app/club", label: "Società", icon: Building2, testid: "nav-club" },
  { to: "/app/calendario", label: "Calendario", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/app/convocazioni", label: "Convocazioni", icon: ClipboardList, testid: "nav-convocations" },
  { to: "/app/analisi", label: "Analisi Post-Match", icon: NotebookPen, testid: "nav-analysis" },
  { to: "/app/presenze", label: "Presenze", icon: CalendarCheck, testid: "nav-attendance" },
  { to: "/app/allenamenti", label: "Allenamenti", icon: Dumbbell, testid: "nav-trainings" },
  { to: "/app/libreria", label: "Libreria esercizi", icon: BookOpen, testid: "nav-library" },
  { to: "/app/statistiche", label: "Statistiche", icon: BarChart3, testid: "nav-stats" },
  { to: "/app/lavagna", label: "Lavagna Tattica", icon: Layout, testid: "nav-tactical" },
  { to: "/app/campi", label: "Campi", icon: MapPin, testid: "nav-fields" },
  { to: "/app/scouting", label: "Ragazzi visionati", icon: Eye, testid: "nav-scouting" },
  { to: "/app/amministrazione", label: "Amministrazione", icon: Wallet, testid: "nav-admin" },
  { to: "/app/membri", label: "Staff & Genitori", icon: UserCog, testid: "nav-members" },
  { to: "/app/notifiche", label: "Notifiche", icon: Bell, testid: "nav-notifications" },
  { to: "/app/chat", label: "Chat famiglie", icon: MessageCircle, testid: "nav-chat" },
  { to: "/app/billing", label: "Piano & Abbonamento", icon: CreditCard, testid: "nav-billing" },
];

// Mobile bottom nav: short labels + most-used routes for quick access
const MOBILE_BOTTOM_NAV = [
  { to: "/app", label: "Home", icon: Home, testid: "nav-dashboard" },
  { to: "/app/anagrafica", label: "Rosa", icon: Users, testid: "nav-players" },
  { to: "/app/convocazioni", label: "Convoc.", icon: ClipboardList, testid: "nav-convocations" },
  { to: "/app/calendario", label: "Calend.", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/app/chat", label: "Chat", icon: MessageCircle, testid: "nav-chat" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { teams, activeTeam, setActive, refresh, loading: teamsLoading, season, setSeason, seasons, addSeason } = useTeams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Under 10");
  const [createSeason, setCreateSeason] = useState(season);
  const [color, setColor] = useState("#0ea5e9");

  // Edit existing team dialog (with logo upload)
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({ name: "", category: "", season: "", color: "#0ea5e9", logo_url: null });

  // Mobile drawer
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Onboarding wizard: auto-open for new users (no completed flag AND no teams)
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardAutoChecked, setWizardAutoChecked] = useState(false);
  useEffect(() => {
    if (wizardAutoChecked) return;
    // Wait until user + teams resolved
    if (!user || user === false) return;
    if (teams === null || teams === undefined) return;
    const dismissedThisSession = sessionStorage.getItem("onboardingDismissed") === "1";
    if (!user.onboarding_completed && teams.length === 0 && !dismissedThisSession) {
      setWizardOpen(true);
    }
    setWizardAutoChecked(true);
  }, [user, teams, wizardAutoChecked]);

  const handleWizardOpenChange = (next) => {
    setWizardOpen(next);
    if (!next) {
      sessionStorage.setItem("onboardingDismissed", "1");
    }
  };

  const handleWizardComplete = async (newTeamId) => {
    await refresh();
    if (newTeamId) setActive(newTeamId);
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    await api.post("/teams", { name, category, season: createSeason, color });
    setOpen(false);
    setName("");
    await refresh();
    toast.success("Squadra creata");
  };

  const openEdit = () => {
    if (!activeTeam) return;
    setEdit({
      name: activeTeam.name || "",
      category: activeTeam.category || "",
      season: activeTeam.season || season,
      color: activeTeam.color || "#0ea5e9",
      logo_url: activeTeam.logo_url || null,
      legal_name: activeTeam.legal_name || "",
      fiscal_code: activeTeam.fiscal_code || "",
      vat_number: activeTeam.vat_number || "",
      address: activeTeam.address || "",
      phone: activeTeam.phone || "",
      email: activeTeam.email || "",
      figc_affiliation_code: activeTeam.figc_affiliation_code || "",
      president_name: activeTeam.president_name || "",
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!activeTeam) return;
    await api.put(`/teams/${activeTeam.id}`, edit);
    toast.success("Squadra aggiornata");
    setEditOpen(false);
    await refresh();
  };

  const handleDeleteTeam = async (team) => {
    const target = team || activeTeam;
    if (!target) return;
    const confirm = window.prompt(
      `⚠️ Stai per ELIMINARE definitivamente la squadra "${target.name}" e TUTTI i dati associati (giocatori, convocazioni, allenamenti, presenze, eventi). Questa azione NON è reversibile.\n\nDigita ESATTAMENTE il nome della squadra per confermare:`
    );
    if (!confirm) return;
    if (confirm.trim() !== target.name) {
      toast.error("Nome non corrispondente. Eliminazione annullata.");
      return;
    }
    try {
      await api.delete(`/teams/${target.id}`);
      toast.success(`Squadra "${target.name}" eliminata`);
      // If the deleted team was active, switch to the first remaining one
      if (activeTeam?.id === target.id) {
        localStorage.removeItem("activeTeamId");
      }
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eliminazione fallita");
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File troppo grande (max 2 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEdit((e) => ({ ...e, logo_url: data.url }));
      toast.success("Logo caricato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const visibleNav = useMemo(() => {
    const isParent = activeTeam?.my_role === "parent";
    const hidden = new Set([
      "nav-members",
      "nav-tactical",
      "nav-admin",
      "nav-staff",
      "nav-scouting",
      "nav-analysis",
      "nav-parents",
      "nav-modulistica",
      "nav-mister-ai",
      "nav-club",
      "nav-signatures",
    ]);
    return isParent ? NAV.filter((item) => !hidden.has(item.testid)) : NAV;
  }, [activeTeam?.my_role]);

  // Unread chat counter (polled every 30s)
  const [chatUnread, setChatUnread] = useState(0);
  useEffect(() => {
    if (!activeTeam?.id) {
      setChatUnread(0);
      return undefined;
    }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get(`/chat/teams/${activeTeam.id}/unread`);
        if (!cancelled) setChatUnread(data?.total || 0);
      } catch (_) {
        if (!cancelled) setChatUnread(0);
      }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [activeTeam?.id]);

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Mobile top bar (hamburger) */}
      <div
        className="md:hidden fixed top-0 inset-x-0 bg-white border-b border-slate-200 flex items-center px-3 gap-3 z-30"
        style={{ paddingTop: "env(safe-area-inset-top)", height: "calc(56px + env(safe-area-inset-top))" }}
      >
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          className="h-10 w-10 rounded-xl bg-slate-50 hover:bg-slate-100 active:bg-slate-200 flex items-center justify-center shrink-0"
          aria-label="Apri menu"
          data-testid="mobile-menu-btn"
          style={{ touchAction: "manipulation" }}
        >
          <Menu size={20} />
        </button>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {activeTeam?.logo_url ? (
            <img
              src={activeTeam.logo_url}
              alt={activeTeam.name}
              className="h-8 w-8 rounded-lg object-cover bg-slate-100 shrink-0"
            />
          ) : (
            <div className="h-8 w-8 rounded-lg bg-sky-500 flex items-center justify-center shrink-0">
              <Trophy size={16} className="text-white" />
            </div>
          )}
          <div className="font-heading font-bold truncate">
            {activeTeam ? activeTeam.name : "Calcio Club"}
          </div>
        </div>
      </div>

      {/* Mobile drawer overlay */}
      {mobileNavOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileNavOpen(false)}
          data-testid="mobile-nav-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`bg-white border-r border-slate-200 flex flex-col z-50
          md:w-64 md:shrink-0 md:static md:flex
          fixed top-0 bottom-0 left-0 w-72 max-w-[85vw]
          transform transition-transform
          ${mobileNavOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
        data-testid="sidebar"
      >
        <div className="p-5 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            {activeTeam?.logo_url ? (
              <img
                src={activeTeam.logo_url}
                alt={activeTeam.name}
                className="h-9 w-9 rounded-xl object-cover bg-slate-100"
                data-testid="sidebar-team-logo"
              />
            ) : (
              <div className="h-9 w-9 rounded-xl bg-sky-500 flex items-center justify-center">
                <Trophy size={18} className="text-white" />
              </div>
            )}
            <div className="font-heading font-bold text-lg leading-none truncate">
              {activeTeam ? activeTeam.name : "Calcio Club"}
            </div>
          </div>
          {/* Season selector */}
          <div className="mt-4">
            <div className="label-eyebrow mb-1.5 flex items-center gap-1">
              <CalendarDays size={11} /> Stagione
            </div>
            <Select
              value={season}
              onValueChange={(v) => {
                if (v === "__add__") {
                  const proposed = (() => {
                    // pick next year after the highest existing season
                    const years = seasons.map((s) => Number(s.split("/")[0]));
                    const max = years.length ? Math.max(...years) : new Date().getFullYear();
                    return `${max + 1}/${max + 2}`;
                  })();
                  const input = window.prompt(
                    "Aggiungi stagione nel formato AAAA/AAAA",
                    proposed
                  );
                  if (!input) return;
                  try {
                    const added = addSeason(input.trim());
                    setSeason(added);
                    toast.success(`Stagione ${added} aggiunta`);
                  } catch (err) {
                    toast.error(err.message || "Formato non valido");
                  }
                  return;
                }
                setSeason(v);
              }}
            >
              <SelectTrigger className="h-9 text-sm" data-testid="season-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {seasons.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
                <SelectItem
                  value="__add__"
                  className="text-sky-600 font-medium"
                  data-testid="season-add-option"
                >
                  + Aggiungi stagione…
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {visibleNav.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/app"}
              data-testid={testid}
              onClick={() => setMobileNavOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${
                  isActive
                    ? "bg-sky-50 text-sky-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <Icon size={18} />
              <span className="flex-1">{label}</span>
              {testid === "nav-chat" && chatUnread > 0 && (
                <span
                  data-testid="nav-chat-unread"
                  className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-sky-500 text-white text-[10px] font-bold flex items-center justify-center"
                >
                  {chatUnread > 99 ? "99+" : chatUnread}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Team switcher (moved to bottom) */}
        <div className="p-4 border-t border-slate-200 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="label-eyebrow">Squadra Attiva</div>
            {activeTeam && activeTeam.my_role !== "parent" && (
              <button
                onClick={openEdit}
                className="text-slate-400 hover:text-sky-600"
                data-testid="edit-team-btn"
                title="Modifica squadra"
              >
                <Settings size={14} />
              </button>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center justify-between gap-2 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition"
                data-testid="team-switcher"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {activeTeam?.logo_url ? (
                    <img
                      src={activeTeam.logo_url}
                      alt={activeTeam.name}
                      className="h-7 w-7 rounded-lg shrink-0 object-cover bg-slate-100"
                    />
                  ) : (
                    <div
                      className="h-7 w-7 rounded-lg shrink-0"
                      style={{ background: activeTeam?.color || "#0ea5e9" }}
                    />
                  )}
                  <div className="text-left min-w-0">
                    <div className="font-medium truncate text-sm">
                      {activeTeam ? activeTeam.name : "Nessuna squadra"}
                    </div>
                    {activeTeam && (
                      <div className="text-xs text-slate-500 truncate">
                        {activeTeam.category}
                        {activeTeam.my_role && activeTeam.my_role !== "owner" && (
                          <span className="ml-1.5 px-1.5 py-0.5 rounded bg-slate-100 text-[10px] uppercase tracking-wider font-bold">
                            {activeTeam.my_role === "director" ? "Dir." : "Genitore"}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <ChevronDown size={16} className="text-slate-400 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="start" side="top" sideOffset={8}>
              {teams.map((t) => (
                <DropdownMenuItem
                  key={t.id}
                  onClick={() => setActive(t.id)}
                  data-testid={`team-option-${t.id}`}
                >
                  {t.logo_url ? (
                    <img
                      src={t.logo_url}
                      alt={t.name}
                      className="h-4 w-4 rounded mr-2 object-cover bg-slate-100"
                    />
                  ) : (
                    <div
                      className="h-4 w-4 rounded mr-2"
                      style={{ background: t.color || "#0ea5e9" }}
                    />
                  )}
                  <div>
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.category}</div>
                  </div>
                </DropdownMenuItem>
              ))}
              {teams.length > 0 && <DropdownMenuSeparator />}
              <DropdownMenuItem onClick={() => setOpen(true)} data-testid="new-team-trigger">
                <Plus size={16} className="mr-2" /> Nuova squadra
              </DropdownMenuItem>
              {activeTeam && activeTeam.my_role === "owner" && (
                <DropdownMenuItem
                  onClick={() => handleDeleteTeam(activeTeam)}
                  data-testid="delete-team-trigger"
                  className="text-red-600 focus:text-red-700 focus:bg-red-50"
                >
                  <Trash2 size={16} className="mr-2" /> Elimina {`"${activeTeam.name}"`}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Footer user */}
        <div className="p-3 border-t border-slate-200">
          <button
            onClick={() => setWizardOpen(true)}
            className="w-full flex items-center gap-2 px-3 py-2 mb-2 rounded-xl text-sm font-medium text-emerald-700 hover:bg-emerald-50 transition"
            data-testid="open-onboarding-wizard"
          >
            <Sparkles size={16} />
            <span>Setup guidato</span>
          </button>
          <div className="flex items-center gap-3 p-2">
            <div className="h-9 w-9 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold">
              {user?.name?.[0]?.toUpperCase() || "M"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name || "Mister"}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
              title="Esci"
            >
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 md:p-8 p-4 pt-20 md:pt-8 pb-24 md:pb-8">
        <PlanProvider teamId={activeTeam?.id}>
          <RenewalBanner />
          <Outlet />
        </PlanProvider>
      </main>

      {/* Mobile bottom nav */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex z-30"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-bottom-nav"
      >
        {MOBILE_BOTTOM_NAV.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/app"}
            data-testid={`${testid}-mobile`}
            className={({ isActive }) =>
              `flex-1 min-w-0 flex flex-col items-center py-2 text-[10px] gap-0.5 ${
                isActive ? "text-sky-600" : "text-slate-500"
              }`
            }
          >
            <Icon size={18} />
            <span className="truncate max-w-full px-1">{label}</span>
          </NavLink>
        ))}
      </div>

      {/* New team dialog */}
      <NewTeamDialog
        open={open}
        onOpenChange={setOpen}
        name={name}
        setName={setName}
        category={category}
        setCategory={setCategory}
        season={createSeason}
        setSeason={setCreateSeason}
        color={color}
        setColor={setColor}
        seasons={seasons}
        onCreate={handleCreate}
      />

      {/* Edit team dialog */}
      <EditTeamDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        edit={edit}
        setEdit={setEdit}
        seasons={seasons}
        onUpload={uploadLogo}
        onSave={handleUpdate}
      />

      {/* Onboarding Wizard */}
      <OnboardingWizard
        open={wizardOpen}
        onOpenChange={handleWizardOpenChange}
        onComplete={handleWizardComplete}
        defaultSeason={season}
        seasons={seasons}
      />

      {/* PWA Install Prompt (Android beforeinstallprompt + iOS Safari fallback) */}
      <PWAInstallPrompt />
    </div>
  );
}
